import withAuth from 'lib/utils/withAuth';
import { useState, useEffect } from 'react';
import Layout from 'components/layout';
import { set, keys } from 'lib/utils/localStorage';
import { reformatLocaleToContentfulLocale } from 'lib/utils/helpers';
import Icon from 'components/shared/icon';
import { useRouter } from 'next/router';
import SalonAccount from 'components/account/salonAccount';
import CustomerAccount from 'components/account/customerAccount/customerAccount';
import useVisitorTypeTracking from 'hooks/tracking/useVisitorTypeTracking';
import { getGlobal } from 'lib/contentful';
import { useWishlist } from 'hooks';
import { getCountryByLocale } from '../../lib/shopify';

const Account = ({ locale, customer, previewData, globalModules }) => {
  const [loading, setLoading] = useState(true);
  const [subscriptionsLinkLoading, setSubscriptionsLinkLoading] = useState(true);
  const [isSalonAccount, setIsSalonAccount] = useState(false);
  const [customerSalons, setCustomerSalons] = useState();
  const [subscriptionsLink, setSubscribrionsLink] = useState(false);
  const { syncWishlists } = useWishlist();

  const router = useRouter();

  const loadSalonAccount = payload => {
    setIsSalonAccount(true);
    setCustomerSalons(payload);
    set(keys.LOGGED_SALON_NO, payload[0].salonNo);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useVisitorTypeTracking({ visitorType: payload[0].salonNo, location: { action: router } });
  };

  useEffect(() => {
    const fetchData = async () => {
      const { email } = customer;

      const response = await fetch(
        `/api/firebase/get-salons-by-email?email=${email}&country=${getCountryByLocale(locale)}`
      );
      const { data } = await response.json();

      if (!data.length) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useVisitorTypeTracking({ visitorType: 'KEUNE Cosmetics', location: { action: router } });
        setIsSalonAccount(false);
      } else {
        if (!data?.[0]?.orders) data[0].orders = [];
        loadSalonAccount(data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    // sync local and shopify wishlists when logged in
    const syncWishlistsOnLogin = async () => {
      await syncWishlists(customer);
    };

    if (customer.email) {
      syncWishlistsOnLogin();
    }
  }, [customer]);

  useEffect(() => {
    if (subscriptionsLink || isSalonAccount) {
      return;
    }
    setSubscriptionsLinkLoading(true);
    const { email } = customer;
    const getMagicLink = async () => {
      const url = '/api/seal-subscriptions/get-magic-link';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      const result = await response.json();

      const link = result?.payload?.magic_link;
      setSubscriptionsLinkLoading(false);

      if (link) {
        setSubscribrionsLink(link);
      }
    };
    getMagicLink();
  }, [customer, isSalonAccount]);

  return (
    <Layout locale={locale} previewData={previewData} globalModules={globalModules}>
      {loading || subscriptionsLinkLoading ? (
        <div
          style={{
            height: '80vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Icon type="loading" />
        </div>
      ) : null}
      {customer && !loading && !subscriptionsLinkLoading ? (
        <>
          {isSalonAccount ? (
            <SalonAccount
              locale={locale}
              customer={customer}
              salons={customerSalons}
              global={{
                account: {
                  ...globalModules['salon-account'],
                  ...globalModules['second-salon-account'],
                },
                payments: globalModules['payments-table'],
                orders: globalModules['orders-table'],
                tables: globalModules.tables,
                noKickbackfeePopup: globalModules['no-kickbackfee-modal'],
                payoutPopup: globalModules['payout-popup'],
              }}
            />
          ) : (
            <CustomerAccount
              locale={locale}
              customer={customer}
              accountpagenavigation={globalModules?.accountpagenavigation}
              dashboardaccountpage={globalModules?.dashboardaccountpage}
              ordersaccountpage={globalModules?.ordersaccountpage}
              orderdetailsaccountpage={globalModules?.orderdetailsaccountpage}
              secondorderdetailsaccountpage={globalModules?.secondorderdetailsaccountpage}
              personalinfoaccountpage={globalModules?.personalinfoaccountpage}
              secondpersonalinfoaccountpage={globalModules?.secondpersonalinfoaccountpage}
              thirdpersonalinfoaccountpage={globalModules?.thirdpersonalinfoaccountpage}
              addressbookaccountpage={globalModules?.addressbookaccountpage}
              addaddressformaccountpage={globalModules?.addaddressformaccountpage}
              secondaddressbookaccountpage={globalModules?.secondaddressbookaccountpage}
              checkout={globalModules?.checkout}
              salonselector={globalModules?.salonselector}
              registermessages={globalModules.registermessages}
              subscriptionsLink={subscriptionsLink}
            />
          )}
        </>
      ) : null}
    </Layout>
  );
};

export const getServerSideProps = withAuth(async ({ customer, previewData = null, locale }) => {
  const globalModules = await getGlobal(
    [
      'accountpagenavigation',
      'dashboardaccountpage',
      'ordersaccountpage',
      'orderdetailsaccountpage',
      'secondorderdetailsaccountpage',
      'personalinfoaccountpage',
      'secondpersonalinfoaccountpage',
      'thirdpersonalinfoaccountpage',
      'addressbookaccountpage',
      'secondaddressbookaccountpage',
      'addaddressformaccountpage',
      'announcement-bar',
      'salon-account',
      'second-salon-account',
      'tables',
      'orders-table',
      'payments-table',
      'cookies',
      'firstcountryselectorlocales',
      'no-kickbackfee-modal',
      'registermessages',
      'payout-popup',
    ],
    reformatLocaleToContentfulLocale(locale)
  );

  const { localesWithWebshop } = globalModules?.settings;

  if (!localesWithWebshop.onOrOff)
    return {
      notFound: true,
    };

  return {
    props: {
      locale,
      customer,
      previewData,
      globalModules,
    },
  };
});

export default Account;
